import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextField } from '@/components/ui/Field';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, LoadingLine } from '@/components/ui/States';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import {
  useCan,
  useCanEditContent,
  useCurrentUser,
} from '@/features/admin-content/use-current-user';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/bank/blueprints')({
  component: BankBlueprintsPage,
});

function BankBlueprintsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const canRead = useCan('content.admin');
  const canWrite = useCanEditContent();
  const [slug, setSlug] = useState('kelp-reading-practice');
  const [variantSlug, setVariantSlug] = useState('');
  const [skill, setSkill] = useState<'reading' | 'listening' | 'writing' | 'speaking'>('reading');
  const [taskTypeId, setTaskTypeId] = useState('');
  const [count, setCount] = useState('1');
  const [timeSeconds, setTimeSeconds] = useState('');

  const exams = useQuery({
    queryKey: ['exams'],
    enabled: canRead,
    queryFn: () => api.get('/api/v1/exams'),
  });
  const variants = exams.data?.exams.flatMap((exam) => exam.variants) ?? [];

  const types = useQuery({
    queryKey: ['admin', 'bank', 'task-types'],
    enabled: canRead,
    queryFn: () => api.get('/api/v1/admin/bank/task-types'),
  });
  const catalog = useQuery({
    queryKey: ['exams', variantSlug, 'blueprints'],
    enabled: canRead && Boolean(variantSlug),
    queryFn: () =>
      api.get('/api/v1/exams/{variant}/blueprints', { params: { variant: variantSlug } }),
  });
  const list = useQuery({
    queryKey: ['admin', 'bank', 'blueprints'],
    enabled: canRead,
    queryFn: () => api.get('/api/v1/admin/bank/blueprints'),
  });

  const typesForSkill = (types.data?.taskTypes ?? []).filter((type) => type.skills.includes(skill));
  const catalogTime = catalog.data?.blueprints
    .find((row) => row.skill === skill)
    ?.sectionSummary.find((section) => section.skill === skill)?.timeSeconds;

  useEffect(() => {
    if (!variantSlug && variants[0]) setVariantSlug(variants[0].slug);
  }, [variantSlug, variants]);

  useEffect(() => {
    if (!typesForSkill.some((type) => type.id === taskTypeId)) {
      const first = typesForSkill[0];
      if (first) setTaskTypeId(first.id);
    }
  }, [skill, taskTypeId, types.data]);

  useEffect(() => {
    if (catalogTime) setTimeSeconds(String(catalogTime));
  }, [catalogTime]);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/v1/admin/bank/blueprints', {
        body: {
          variantSlug,
          slug,
          kind: 'practice',
          skill,
          status: 'draft',
          definition: {
            sections: [
              {
                skill,
                delivery: 'free',
                timeSeconds: Number(timeSeconds),
                slots: [{ taskType: taskTypeId, count: Number(count) }],
              },
            ],
          },
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', 'blueprints'] });
      toast({ message: 'Draft form saved', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'We could not save that form', tone: 'error' });
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) =>
      api.post('/api/v1/admin/bank/blueprints/{id}/activate', { params: { id } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', 'blueprints'] });
      toast({ message: 'Students can start this form', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'We could not make that form active', tone: 'error' });
    },
  });

  if (sessionPending || meQuery.isPending) return <LoadingLine>Loading</LoadingLine>;
  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }
  if (!canRead) return <StaffOnly />;

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title="Practice and mock forms"
        description="A form is a blueprint: skill, timing and question counts. Drafts stay hidden from students."
      />

      {list.isError ? (
        <Alert tone="danger" title="We could not load forms">
          Try again in a moment.
        </Alert>
      ) : null}
      {list.data && list.data.blueprints.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No staff forms yet"
          description="Create a draft, then make it active."
        />
      ) : null}
      {list.data && list.data.blueprints.length > 0 ? (
        <Card padding="none">
          <Table>
            <TableHead>
              <TableHeader>Slug</TableHeader>
              <TableHeader>Kind</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader> </TableHeader>
            </TableHead>
            <TableBody>
              {list.data.blueprints.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.slug}</TableCell>
                  <TableCell>{row.kind}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    {canWrite && row.status === 'draft' ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          activate.mutate(row.id);
                        }}
                      >
                        Make active
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      {canWrite ? (
        <Card padding="lg" className="grid gap-4">
          <h2 className="font-display text-h3">Create a draft form</h2>
          <SelectField
            label="Exam variant"
            value={variantSlug}
            onChange={(event) => {
              setVariantSlug(event.target.value);
            }}
          >
            {variants.map((variant) => (
              <option key={variant.slug} value={variant.slug}>
                {variant.name}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Slug"
            value={slug}
            onChange={(event) => {
              setSlug(event.target.value);
            }}
          />
          <SelectField
            label="Skill"
            value={skill}
            onChange={(event) => {
              setSkill(event.target.value as typeof skill);
            }}
          >
            <option value="reading">Reading</option>
            <option value="listening">Listening</option>
            <option value="writing">Writing</option>
            <option value="speaking">Speaking</option>
          </SelectField>
          <SelectField
            label="Task type"
            value={taskTypeId}
            onChange={(event) => {
              setTaskTypeId(event.target.value);
            }}
          >
            {typesForSkill.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Question count"
            value={count}
            onChange={(event) => {
              setCount(event.target.value);
            }}
          />
          <TextField
            label="Time (seconds)"
            hint="Copied from an existing catalog form for this skill. Change only if that form is wrong."
            value={timeSeconds}
            onChange={(event) => {
              setTimeSeconds(event.target.value);
            }}
          />
          <div>
            <Button
              type="button"
              loading={create.isPending}
              disabled={!variantSlug || !taskTypeId || !timeSeconds}
              onClick={() => {
                create.mutate();
              }}
            >
              Save draft form
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
